import React, { useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { Link } from 'react-scroll';
import profileImg from '../assets/lovette.jpeg';

const socials = [
	{ icon: 'mdi:github', href: 'https://www.github.com/kimbohlovette', label: 'GitHub' },
	{ icon: 'ps:linkedin', href: 'https://www.linkedin.com/in/kimbohlovette', label: 'LinkedIn' },
	{ icon: 'mdi:twitter', href: 'https://www.twitter.com/kimbohlovette', label: 'Twitter' },
];

const stack = ['Go', 'TypeScript', 'React', 'AWS', 'PostgreSQL', 'gRPC'];

function Hero() {
	const canvasRef = useRef<HTMLCanvasElement>(null);

	// Animated particle grid
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		let animId: number;
		const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
		resize();
		window.addEventListener('resize', resize);

		const dots: { x: number; y: number; vx: number; vy: number; r: number }[] = [];
		for (let i = 0; i < 60; i++) {
			dots.push({ x: Math.random() * canvas.width, y: Math.random() * canvas.height, vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3, r: Math.random() * 1.5 + 0.5 });
		}

		const draw = () => {
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			dots.forEach(d => {
				d.x += d.vx; d.y += d.vy;
				if (d.x < 0 || d.x > canvas.width) d.vx *= -1;
				if (d.y < 0 || d.y > canvas.height) d.vy *= -1;
				ctx.beginPath();
				ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
				ctx.fillStyle = 'rgba(99,102,241,0.5)';
				ctx.fill();
			});
			dots.forEach((a, i) => dots.slice(i + 1).forEach(b => {
				const dist = Math.hypot(a.x - b.x, a.y - b.y);
				if (dist < 120) {
					ctx.beginPath();
					ctx.moveTo(a.x, a.y);
					ctx.lineTo(b.x, b.y);
					ctx.strokeStyle = `rgba(99,102,241,${0.15 * (1 - dist / 120)})`;
					ctx.lineWidth = 0.5;
					ctx.stroke();
				}
			}));
			animId = requestAnimationFrame(draw);
		};
		draw();
		return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
	}, []);

	return (
		<section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
			{/* Background */}
			<div className="absolute inset-0 bg-primary">
				<div className="orb w-[600px] h-[600px] bg-accent/20 -top-40 -left-40" />
				<div className="orb w-[500px] h-[500px] bg-accent-2/10 top-1/2 -right-40" />
				<canvas ref={canvasRef} className="absolute inset-0 w-full h-full opacity-60" />
			</div>

			<div className="relative z-10 max-w-6xl mx-auto px-6 w-full pt-24 pb-16">
				<div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-20">
					{/* Text */}
					<div className="flex-1 text-center lg:text-left">
						<div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-accent/30 bg-accent/10 text-accent text-xs font-semibold mb-6 animate-fade-in">
							<span className="w-1.5 h-1.5 rounded-full bg-accent animate-breathe" />
							Available for new opportunities
						</div>

						<h1 className="font-display font-bold leading-[1.05] mb-4 animate-fade-up" style={{ fontSize: 'clamp(2.8rem, 6vw, 5rem)' }}>
							<span className="text-white">Kimboh</span><br />
							<span className="gradient-text">Lovette</span>
						</h1>

						<p className="text-slate-400 text-lg font-light max-w-xl mb-4 animate-fade-up" style={{ animationDelay: '0.15s', opacity: 0, animationFillMode: 'forwards' }}>
							I turn complex problems into elegant, scalable software —{' '}
							<span className="text-slate-200 font-medium">4+ years</span> shipping production systems across fintech, travel, and SaaS.
						</p>

						{/* Stack pills */}
						<div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-8 animate-fade-up" style={{ animationDelay: '0.3s', opacity: 0, animationFillMode: 'forwards' }}>
							{stack.map(s => <span key={s} className="tag">{s}</span>)}
						</div>

						<div className="flex flex-wrap gap-3 justify-center lg:justify-start mb-10 animate-fade-up" style={{ animationDelay: '0.45s', opacity: 0, animationFillMode: 'forwards' }}>
							<Link to="contacts" smooth offset={-80} duration={600}>
								<button className="btn-primary">
									<Icon icon="fluent:mail-24-filled" />
									Get in touch
								</button>
							</Link>
							<a href="https://drive.google.com/file/d/1sZL5kAwmhzgzsdsrki3DeRywPJBBETeo/view?usp=sharing" target="_blank" rel="noreferrer">
								<button className="btn-ghost">
									<Icon icon="mdi:file-document-outline" />
									View Resume
								</button>
							</a>
						</div>

						<div className="flex items-center gap-5 justify-center lg:justify-start animate-fade-up" style={{ animationDelay: '0.6s', opacity: 0, animationFillMode: 'forwards' }}>
							{socials.map(s => (
								<a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label}
									className="text-slate-500 hover:text-white transition-colors duration-500 text-xl">
									<Icon icon={s.icon} />
								</a>
							))}
						</div>
					</div>

					{/* Photo */}
					<div className="flex-shrink-0 animate-float">
						<div className="relative">
							{/* Slow-orbiting ring — CSS transform, not spin */}
							<div className="absolute inset-[-20px] rounded-full border border-accent/20 animate-orbit" style={{ borderStyle: 'dashed' }} />
							<div className="absolute inset-[-38px] rounded-full border border-accent-2/10 animate-orbit-reverse" style={{ borderStyle: 'dashed' }} />
							{/* Glow */}
							<div className="absolute inset-0 rounded-full bg-accent/15 blur-3xl scale-110 animate-breathe" />
							<img src={profileImg} alt="Kimboh Lovette"
								className="relative w-56 h-56 sm:w-72 sm:h-72 object-cover rounded-full border-2 border-accent/30 shadow-glow brightness-90"
							/>
							{/* Badge */}
							<div className="absolute -bottom-3 -right-3 glass-card px-3 py-2 flex items-center gap-2 text-xs font-semibold text-white">
								<span className="w-2 h-2 rounded-full bg-emerald-400 animate-breathe" />
								Open to work
							</div>
						</div>
					</div>
				</div>

				{/* Scroll indicator */}
				<div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-600 animate-scroll-hint">
					<span className="text-xs tracking-widest uppercase">Scroll</span>
					<Icon icon="mdi:chevron-down" className="text-lg" />
				</div>
			</div>
		</section>
	);
}

export default Hero;
